import _ from 'lodash';
import React, {FunctionComponent, useContext} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {ProductTileView} from '@treez-ui/core/product/productTile/ProductTileView';
import {Paginator} from '@treez-ui/core/table';
import ProductTable, {ProductTableColumn} from '@treez-ui/core/table/ProductTable';
import {Showcase} from '@treez-ui/core/sharedTypings';
import {determineIfAllProductsSelected, ProductListView} from '~/lib/product';
import {generateQueryString, parseQueryString} from '~/lib/routing';
import {
    CurrentSelectionStateContext,
    DispatchCurrentSelectionAction,
    TypeOfSelectAction,
} from '~/lib/selectedProductContext';
import {
    amountColumn,
    generateBrandColumn,
    generateLinkActionColumn,
    generateSelectColumn,
    generateNameColumn,
    skuColumn,
    subtypeColumn,
    typeColumn,
} from '~/modules/widgets/products/ProductTableColumns';

const {ADD, CLEAR, REMOVE} = TypeOfSelectAction;

interface Props extends Showcase<ProductInCatalog> {
    productCount: number;
    linkProduct: (brandProduct: ProductInCatalog) => void;
    productToLink?: ProductInCatalog;
    inspectProduct: (productToInspect: ProductInCatalog) => void;
}

const {TABLE, TILE} = ProductListView;

export const BrandProducts: FunctionComponent<Props> = (props: Props) => {
    const {
        values: products = [],
        fetching,
        productCount,
        linkProduct,
        productToLink,
        inspectProduct,
    } = props;

    const history = useHistory();
    const location = useLocation();

    const queryParams = parseQueryString(location.search);

    const {view = TILE, page = 1, pageSize = 10} = queryParams;

    const selectedProducts = useContext(CurrentSelectionStateContext);
    const dispatchSelectAction = DispatchCurrentSelectionAction();

    const selectedProductIds = _.map(selectedProducts, 'id');

    const handleSelect = (isSelected: boolean, productSelected: ProductInCatalog) => {
        dispatchSelectAction({
            type: isSelected ? ADD : REMOVE,
            products: [productSelected],
        });
    };

    const selectAll = (isSelected: boolean) => {
        if (isSelected) {
            dispatchSelectAction({
                type: ADD,
                products,
            });
        } else {
            dispatchSelectAction({type: CLEAR});
        }
    };

    const allSelected = determineIfAllProductsSelected(products, selectedProductIds);

    // Nav
    const handlePaginationChange = (newPageNumber: number, newPageSize: number) => {
        history.replace({
            search: generateQueryString({
                ...queryParams,
                page: newPageNumber,
                pageSize: newPageSize,
            }),
        });
    };

    const baseFields = [
        generateNameColumn(inspectProduct),
        generateBrandColumn(inspectProduct),
        amountColumn,
        typeColumn,
        subtypeColumn,
        skuColumn,
    ];

    const columnsForLinking = [generateLinkActionColumn(linkProduct), ...baseFields];

    const columnsForBrowsing = [
        generateSelectColumn(selectAll, allSelected, handleSelect),
        ...baseFields,
    ];

    // Render Functions and Values
    const productTableColumns = productToLink == null ? columnsForBrowsing : columnsForLinking;

    const noProductsFound = fetching === false && products.length === 0;
    return (
        <div className="brand-products-container">
            {view === TABLE && (
                <ProductTable
                    columns={productTableColumns as ProductTableColumn[]}
                    fetching={fetching}
                    selectedValueIds={selectedProductIds}
                    values={products}
                />
            )}
            {view === TILE && (
                <ProductTileView
                    fetching={fetching}
                    selectedValueIds={selectedProductIds}
                    values={products}
                    select={handleSelect}
                    onClick={inspectProduct}
                />
            )}
            {fetching === false && noProductsFound === false && (
                <Paginator
                    pageSize={pageSize as number}
                    pageIdx={page as number}
                    numItems={productCount}
                    onChange={handlePaginationChange}
                />
            )}
        </div>
    );
};

export default BrandProducts;
